import { WebhookType } from '../../types/SettingsType'

interface IIntegrationModalProps {
  setShowIntegrationModal: (show: boolean) => void
  screenToShow: string
  revokeKey?: () => void
  regenerateKey?: () => void
  cancelDeactivateWebhook?: () => void
  deactivateWebhook?: (type: WebhookType) => void
  removeConditionalAccess?: () => void
  disableConditionalAccess?: () => void
  cancelRemoveConditionalAccess?: () => void
  removeDevicePolicies?: () => void
  cancelRemoveDevicePolicies?: () => void
  disableDevicePolicy?: () => void
  cancelDisablePolicy?: () => void

  webhookType?: WebhookType
}

function IntegrationModal(props: IIntegrationModalProps) {
  const {
    setShowIntegrationModal,
    revokeKey,
    cancelDeactivateWebhook,
    regenerateKey,
    screenToShow,
    deactivateWebhook,
    removeConditionalAccess,
    disableConditionalAccess,
    cancelRemoveConditionalAccess,
    removeDevicePolicies,
    cancelRemoveDevicePolicies,
    disableDevicePolicy,
    cancelDisablePolicy,
    webhookType,
  } = props

  const defocusModal = () => {
    cancelRemoveConditionalAccess && cancelRemoveConditionalAccess()
    cancelDeactivateWebhook && cancelDeactivateWebhook()
    cancelRemoveDevicePolicies && cancelRemoveDevicePolicies()
    setShowIntegrationModal(false)
  }

  const webhookHumanReadable = (type: WebhookType) => {
    switch (type) {
      case 'ALERT':
        return 'alert'
      case 'DEVICE_STATE':
        return 'device state'
      case 'AUDIT_LOG':
        return 'audit log'
      default:
        return 'device state'
    }
  }

  return (
    <div className="bg-black bg-opacity-50 absolute top-0 left-0 w-full h-full z-50" onClick={defocusModal}>
      <div
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-modalBackground rounded-lg p-6 max-h-[50%] overflow-y-auto border dark:border-gray-600"
        onClick={(e) => e.stopPropagation()}
      >
        {screenToShow === 'REGENERATE' && (
          <>
            <h2 className="text-xl font-bold mb-4">Re-generate API Key</h2>
            <p>Are you sure you want to generate a new API token?</p>
            <p className="mb-4">All services using the current API key will be invalidated.</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 mr-2 bg-gray-300 rounded-lg text-neutral-12"
                onClick={() => {
                  setShowIntegrationModal(false)
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-ErrorRed text-white rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  regenerateKey!()
                }}
              >
                Yes, Re-generate
              </button>
            </div>
          </>
        )}
        {screenToShow === 'REVOKE' && (
          <>
            <h2 className="text-xl font-bold mb-4">Revoke API Access</h2>
            <p>Are you sure you want to revoke your access to the API?</p>
            <p className="mb-4">All services using the current API key will be revoked.</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 mr-2 bg-gray-300 rounded-lg text-neutral-12"
                onClick={() => setShowIntegrationModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-ErrorRed text-white rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  revokeKey!()
                }}
              >
                Yes, Revoke
              </button>
            </div>
          </>
        )}
        {screenToShow === 'WEBHOOK' && (
          <>
            <h2 className="text-xl font-bold mb-4 capitalize">{`Deactivate ${webhookHumanReadable(webhookType!)} Webhook`}</h2>
            <p>{`Are you sure you want to deactivate the ${webhookHumanReadable(webhookType!)} webhook?`}</p>
            <p className="mb-4">{`You will no longer receive ${webhookHumanReadable(webhookType!)} webhook notifications.`}</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 mr-2 bg-gray-300 rounded-lg text-neutral-12"
                onClick={() => {
                  cancelDeactivateWebhook!()
                  setShowIntegrationModal(false)
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-ErrorRed text-white rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  deactivateWebhook!(webhookType!)
                }}
              >
                Yes, Deactivate
              </button>
            </div>
          </>
        )}
        {screenToShow === 'CONDITIONAL_ACCESS' && (
          <>
            <h2 className="text-xl font-bold mb-4">Remove Conditional Access</h2>
            <p>This will reinstate access to all suspended members and their activated devices,</p>
            <p className="mb-4">
              close any open alerts, and remove all conditional access settings for your organization.
            </p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 mr-2 bg-gray-300 rounded-lg text-neutral-12"
                onClick={() => {
                  setShowIntegrationModal(false)
                  cancelRemoveConditionalAccess!()
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-ErrorRed text-white rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  removeConditionalAccess!()
                }}
              >
                Remove
              </button>
            </div>
          </>
        )}
        {screenToShow === 'CONDITIONAL_ACCESS_DISABLE' && (
          <>
            <h2 className="text-xl font-bold mb-4">Disable Conditional Access</h2>
            <p>This will reinstate access to all suspended members and their activated devices,</p>
            <p className="mb-4">as well as close any open alerts.</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 mr-2 bg-gray-300 rounded-lg text-neutral-12"
                onClick={() => {
                  setShowIntegrationModal(false)
                  cancelRemoveConditionalAccess!()
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-ErrorRed text-white rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  disableConditionalAccess!()
                }}
              >
                Disable
              </button>
            </div>
          </>
        )}
        {screenToShow === 'DEVICE_POLICIES_REMOVE' && (
          <>
            <h2 className="text-xl font-bold mb-4">Remove Device Policies</h2>
            <p className="mb-4">This will reinstate access to all suspended members and their activated devices, as well as close any open alerts.</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 mr-2 bg-gray-300 rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  cancelRemoveDevicePolicies!()
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-ErrorRed text-white rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  removeDevicePolicies!()
                }}
              >
                Remove
              </button>
            </div>
          </>
        )}
         {screenToShow === 'DEVICE_POLICIES_ALERT_UPDATE' && (
          <>
            <h2 className="text-xl font-bold mb-4">Update Device Policies</h2>
            <p className="mb-4">This will reinstate access to members suspended by this policy, as well as close any open alerts.</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 mr-2 bg-gray-300 rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  cancelDisablePolicy!()
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-ErrorRed text-white rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  disableDevicePolicy!()
                }}
              >
                Save
              </button>
            </div>
          </>
        )}
        {screenToShow === 'DEVICE_POLICIES_UPDATE' && (
          <>
            <h2 className="text-xl font-bold mb-4">Update Device Policies</h2>
            <p className="mb-4">This will reinstate access to members suspended by this policy.</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 mr-2 bg-gray-300 rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  cancelDisablePolicy!()
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-ErrorRed text-white rounded-lg"
                onClick={() => {
                  setShowIntegrationModal(false)
                  disableDevicePolicy!()
                }}
              >
                Save
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default IntegrationModal
